/**
 * CompanyAdminStats — Stats tab for the Company Admin portal.
 * Mirrors AdminStats but hard-scoped to the user's companyId (no company picker).
 */

import React, { useState, useEffect } from "react";
import { updateUserSettings } from "../../utils/firestoreService";
import { getCompanyAdminUsageSummary } from "../../utils/statsService";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import LinearProgress from "@mui/material/LinearProgress";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { FileText, Users, Mic } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

// Recharts SVG presentation attributes do not reliably resolve theme variables.
const CHART_COLOR = "#1976d2";

const CompanyAdminStats = ({ companyId, userId, adminPrefs, setAdminPrefs }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [period, setPeriod] = useState(adminPrefs?.statsPeriod || "week");
  const [selectedChart, setSelectedChart] = useState(adminPrefs?.statsSelectedChart || "documents");

  const [docsCreated, setDocsCreated] = useState(0);
  const [docsCreatedByDay, setDocsCreatedByDay] = useState([]);
  const [activeUsers, setActiveUsers] = useState(0);
  const [totalUsers, setTotalUsers] = useState(0);
  const [streamingStats, setStreamingStats] = useState({ avgDurationMin: 0, sessionCount: 0 });
  const [usersByDay, setUsersByDay] = useState([]);
  const [sessionsByDay, setSessionsByDay] = useState([]);
  const [documentInsights, setDocumentInsights] = useState({
    categoryBreakdown: [],
    typeBreakdown: [],
    documentingUsers: 0,
    patientsDocumented: 0,
  });
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState(null);

  // Helper to save preferences
  const savePref = (key, value) => {
    if (userId && setAdminPrefs) {
      const updatedPrefs = { ...adminPrefs, [key]: value };
      setAdminPrefs(updatedPrefs);
      updateUserSettings(userId, { companyAdminPreferences: updatedPrefs });
    }
  };

  // The backend derives company scope from the authenticated user record.
  useEffect(() => {
    let cancelled = false;

    const fetchStats = async () => {
      setStatsLoading(true);
      setStatsError(null);
      setDocsCreated(0);
      setDocsCreatedByDay([]);
      setActiveUsers(0);
      setTotalUsers(0);
      setStreamingStats({ avgDurationMin: 0, sessionCount: 0 });
      setUsersByDay([]);
      setSessionsByDay([]);
      setDocumentInsights({
        categoryBreakdown: [],
        typeBreakdown: [],
        documentingUsers: 0,
        patientsDocumented: 0,
      });
      try {
        const summary = await getCompanyAdminUsageSummary(period);
        if (cancelled) return;
        setDocsCreated(summary.documentsCreated);
        setDocsCreatedByDay(summary.documentsCreatedByDay);
        setActiveUsers(summary.activeUsers);
        setTotalUsers(summary.totalUsers);
        setStreamingStats(summary.streamingStats);
        setUsersByDay(summary.activeUsersByDay);
        setSessionsByDay(summary.sessionsByDay);
        setDocumentInsights({
          categoryBreakdown: summary.documentCategoryBreakdown,
          typeBreakdown: summary.documentTypeBreakdown,
          documentingUsers: summary.documentingUsers,
          patientsDocumented: summary.patientsDocumented,
        });
      } catch (error) {
        console.error("Error fetching company stats:", error);
        if (!cancelled) {
          setStatsError("Organization statistics could not be loaded. Please try again.");
        }
      } finally {
        if (!cancelled) {
          setStatsLoading(false);
          setIsLoading(false);
        }
      }
    };

    if (companyId) {
      fetchStats();
    } else {
      setDocsCreated(0);
      setDocsCreatedByDay([]);
      setActiveUsers(0);
      setTotalUsers(0);
      setStreamingStats({ avgDurationMin: 0, sessionCount: 0 });
      setUsersByDay([]);
      setSessionsByDay([]);
      setDocumentInsights({
        categoryBreakdown: [],
        typeBreakdown: [],
        documentingUsers: 0,
        patientsDocumented: 0,
      });
      setStatsError("Your account is not assigned to an organization.");
      setStatsLoading(false);
      setIsLoading(false);
    }

    return () => {
      cancelled = true;
    };
  }, [period, companyId]);

  if (isLoading) {
    return (
      <div className="standard-container">
        <Box sx={{ width: "100%", mt: 4 }}>
          <LinearProgress />
        </Box>
      </div>
    );
  }

  return (
    <div className="standard-container">
      {/* Period toggle */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, mt: 2, alignItems: "center" }}>
        <ToggleButtonGroup
          value={period}
          exclusive
          onChange={(e, newPeriod) => {
            if (newPeriod) {
              setPeriod(newPeriod);
              savePref("statsPeriod", newPeriod);
            }
          }}
          size="small"
        >
          <ToggleButton value="today">Today</ToggleButton>
          <ToggleButton value="week">Week</ToggleButton>
          <ToggleButton value="month">Month</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {statsError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {statsError}
        </Alert>
      )}

      {/* Empty-state notice */}
      {!statsLoading && !statsError && docsCreated === 0 && activeUsers === 0 && streamingStats.sessionCount === 0 && (
        <Box sx={{ mb: 2, p: 1.5, bgcolor: "warning.light", borderRadius: 1, opacity: 0.9 }}>
          <Typography variant="body2" color="warning.dark">
            No activity found for your organization in the selected period.
          </Typography>
        </Box>
      )}

      {/* Stat Cards */}
      <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
        {/* Documents */}
        <Paper
          onClick={() => { setSelectedChart("documents"); savePref("statsSelectedChart", "documents"); }}
          sx={{
            p: 3, minWidth: 200, display: "flex", flexDirection: "column", alignItems: "center", gap: 1,
            cursor: "pointer", border: "2px solid",
            borderColor: selectedChart === "documents" ? "var(--color-primary)" : "transparent",
            transition: "border-color 0.2s",
            "&:hover": { borderColor: selectedChart === "documents" ? "var(--color-primary)" : "var(--color-primary-light, #ccc)" },
          }}
        >
          <FileText size={32} color="var(--color-primary)" />
          <Typography variant="h4" sx={{ fontWeight: "bold" }}>{statsLoading ? "..." : docsCreated}</Typography>
          <Typography variant="body2" color="text.secondary">Documents Created</Typography>
          <Typography variant="caption" color="text.secondary" component="div" sx={{ borderTop: "1px solid #eee", pt: 1, mt: 1, width: "100%", textAlign: "center", lineHeight: 1.6 }}>
            {documentInsights.categoryBreakdown.map((bucket, index) => (
              <React.Fragment key={bucket.key}>
                {index > 0 && " · "}
                {bucket.label} {bucket.count}
              </React.Fragment>
            ))}
          </Typography>
        </Paper>

        {/* Users */}
        <Paper
          onClick={() => { setSelectedChart("users"); savePref("statsSelectedChart", "users"); }}
          sx={{
            p: 3, minWidth: 200, display: "flex", flexDirection: "column", alignItems: "center", gap: 1,
            cursor: "pointer", border: "2px solid",
            borderColor: selectedChart === "users" ? "var(--color-primary)" : "transparent",
            transition: "border-color 0.2s",
            "&:hover": { borderColor: selectedChart === "users" ? "var(--color-primary)" : "var(--color-primary-light, #ccc)" },
          }}
        >
          <Users size={32} color="var(--color-primary)" />
          <Typography variant="h4" sx={{ fontWeight: "bold" }}>{statsLoading ? "..." : activeUsers}</Typography>
          <Typography variant="body2" color="text.secondary">Active Users</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ borderTop: "1px solid #eee", pt: 1, mt: 1, width: "100%", textAlign: "center" }}>
            {totalUsers} total
          </Typography>
        </Paper>

        {/* Sessions */}
        <Paper
          onClick={() => { setSelectedChart("sessions"); savePref("statsSelectedChart", "sessions"); }}
          sx={{
            p: 3, minWidth: 200, display: "flex", flexDirection: "column", alignItems: "center", gap: 1,
            cursor: "pointer", border: "2px solid",
            borderColor: selectedChart === "sessions" ? "var(--color-primary)" : "transparent",
            transition: "border-color 0.2s",
            "&:hover": { borderColor: selectedChart === "sessions" ? "var(--color-primary)" : "var(--color-primary-light, #ccc)" },
          }}
        >
          <Mic size={32} color="var(--color-primary)" />
          <Typography variant="h4" sx={{ fontWeight: "bold" }}>{statsLoading ? "..." : `${streamingStats.avgDurationMin}`}</Typography>
          <Typography variant="body2" color="text.secondary">Avg Session (min)</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ borderTop: "1px solid #eee", pt: 1, mt: 1, width: "100%", textAlign: "center" }}>
            {streamingStats.sessionCount} sessions
          </Typography>
        </Paper>
      </Box>

      {/* Chart */}
      {!statsLoading && (
        <Paper sx={{ p: 3, mt: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {selectedChart === "documents" && `Documents Created (${period === "today" ? "Today" : period === "week" ? "Last 7 Days" : "Last 30 Days"})`}
            {selectedChart === "users" && `Active Users (${period === "today" ? "Today" : period === "week" ? "Last 7 Days" : "Last 30 Days"})`}
            {selectedChart === "sessions" && `Streaming Sessions (${period === "today" ? "Today" : period === "week" ? "Last 7 Days" : "Last 30 Days"})`}
          </Typography>
          {period === "today" ? (
            <Box sx={{ height: 100, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Typography color="text.secondary">Select Week or Month to view chart trends</Typography>
            </Box>
          ) : (() => {
            const chartData =
              selectedChart === "documents" ? docsCreatedByDay :
              selectedChart === "users" ? usersByDay : sessionsByDay;
            const hasData = chartData.some((d) => d.count > 0);

            if (!hasData) {
              return (
                <Box sx={{ height: 300, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Typography color="text.secondary">No data for this period</Typography>
                </Box>
              );
            }

            return (
              <Box sx={{ width: "100%", height: 300, minHeight: 300, minWidth: 0 }}>
                <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="label" tick={{ fontSize: 12 }} interval={period === "month" ? 4 : 0} />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Line
                      type="monotone"
                      dataKey="count"
                      stroke={CHART_COLOR}
                      strokeWidth={2}
                      dot={{ fill: CHART_COLOR }}
                      name={
                        selectedChart === "documents" ? "Documents" :
                        selectedChart === "users" ? "Users" : "Sessions"
                      }
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            );
          })()}
          {selectedChart === "documents" && !statsError && (
            <Box sx={{ borderTop: "1px solid #e0e0e0", mt: 2, pt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Document Type Breakdown
              </Typography>
              {documentInsights.typeBreakdown.length > 0 ? (
                <Box sx={{ display: "grid", gridTemplateColumns: "minmax(180px, 1fr) auto auto", columnGap: 3, rowGap: 0.75 }}>
                  <Typography variant="caption" color="text.secondary">Document type</Typography>
                  <Typography variant="caption" color="text.secondary" align="right">Documents</Typography>
                  <Typography variant="caption" color="text.secondary" align="right">Share</Typography>
                  {documentInsights.typeBreakdown.map((type) => (
                    <React.Fragment key={`${type.typeId || type.name}-${type.category}`}>
                      <Typography variant="body2">{type.name}</Typography>
                      <Typography variant="body2" align="right">{type.count}</Typography>
                      <Typography variant="body2" align="right">
                        {docsCreated > 0 ? `${Math.round((type.count / docsCreated) * 100)}%` : "0%"}
                      </Typography>
                    </React.Fragment>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No document types recorded for this period
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1.5 }}>
                {documentInsights.documentingUsers} of {totalUsers} users created documents · {documentInsights.patientsDocumented} patients documented
              </Typography>
            </Box>
          )}
        </Paper>
      )}
    </div>
  );
};

export default CompanyAdminStats;
